//import { DEMO_POSTS } from "data/posts";
//import { PostDataType } from "data/types";
import React, { useEffect, useState } from "react";
import hljs from 'highlight.js';
import 'highlight.js/styles/monokai-sublime.css';
import DOMPurify from 'dompurify';
import { useNavigate, useParams } from "react-router-dom";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import BlogComment, { Comment } from "./BlogComment";
import NcImage from "shared/NcImage/NcImage";
import Textarea from "shared/Textarea/Textarea";
import DeleteModal from "containers/BlogPage/BlogModal/DeleteModal";
import { useAuth } from "components/AuthLogin/AuthContext";

const BlogSingle = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user, getCookie } = useAuth();
  const currentUserId = getCookie('userId');
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${id}`);
        if (!response.ok) throw new Error('Failed to fetch post');
        const data = await response.json();
        setPost(data);

        await fetch(`${process.env.REACT_APP_API_URL}/posts/count`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ post: { ...data, type: 'view' } })
        });

        const commentsRes = await fetch(`${process.env.REACT_APP_API_URL}/posts/${id}/comments`);
        if (commentsRes.ok) {
          setComments(await commentsRes.json());
        }
      } catch (err) {
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Techfeed`;
    }
    return () => { document.title = 'Techfeed'; };
  }, [post]);

  useEffect(() => {
    if (post) {
      document.querySelectorAll('#single-entry-content pre').forEach((block) => {
        if (block instanceof HTMLElement && !block.dataset.highlighted) {
          hljs.highlightElement(block);
        }
      });
    }
  }, [post]);

  if (loading) return (
    <div className="nc-PageSingle pt-8 lg:pt-16 animate-pulse">
      <div className="container max-w-screen-md mx-auto space-y-5 px-4">
        <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-24" />
        <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
        <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
        <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-5/6" />
        <div className="flex items-center space-x-3 pt-2">
          <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
          <div className="space-y-2">
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-32" />
            <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-24" />
          </div>
        </div>
      </div>
      <div className="container my-10 sm:my-12 max-w-4xl mx-auto">
        <div className="w-full h-64 sm:h-96 bg-neutral-200 dark:bg-neutral-700 rounded-xl" />
      </div>
      <div className="container max-w-screen-md mx-auto space-y-4 px-4">
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-4/5" />
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
      </div>
    </div>
  );
  if (!post) return null;

  const handleModify = () => {
    navigate('/blog-modify', { state: { post } });
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${post.id}`, {
        method: "DELETE",
        headers: {
          'Authorization': `Bearer ${getCookie('apiToken')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Delete operation failed");
      }

      navigate('/blog');
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmittingComment) return;
    const apiToken = getCookie('apiToken');
    setIsSubmittingComment(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (apiToken) headers['Authorization'] = `Bearer ${apiToken}`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${post.id}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          comment: newComment,
          content_id: post.id,
          parent_id: replyToId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit comment');
      }

      const data: Comment = await response.json();
      setComments([...comments, data]);
      setNewComment("");
      setReplyToId(null);
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentDelete = async (commentId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${post.id}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getCookie('apiToken')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      setComments(comments.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const renderHeader = () => {
    return (
      <header className="container rounded-xl">
        <div className="max-w-screen-md mx-auto space-y-5">
          {post.categories?.[0] && (
            <Badge color={post.categories[0].color} name={post.categories[0].category} />
          )}
          <h1
            className=" text-neutral-900 font-semibold text-3xl md:text-4xl md:!leading-[120%] lg:text-4xl dark:text-neutral-100 max-w-4xl "
            title={post.title}
          >
            {post.title}
          </h1>
          <span className="block text-base text-neutral-500 md:text-lg dark:text-neutral-400 pb-1">
            {post.desc}
          </span>

          <div className="w-full border-b border-neutral-100 dark:border-neutral-800"></div>
          <div className="flex flex-col items-baseline sm:flex-row sm:justify-between">
            <div
                className="nc-PostMeta2 flex items-center flex-wrap text-neutral-700 text-left dark:text-neutral-200 text-sm leading-none flex-shrink-0">
              <Avatar
                  containerClassName="flex-shrink-0"
                  sizeClass="w-8 h-8 sm:h-11 sm:w-11 "
                  imgUrl={post.author.avatar}
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <span className="block font-semibold">
                    {post.author.displayName}
                  </span>
                </div>
                <div className="text-xs mt-[6px]">
                  <span className="text-neutral-700 dark:text-neutral-300">
                    {post.date}
                  </span>
                  {/*<span className="mx-2 font-semibold">·</span>
                  <span className="text-neutral-700 dark:text-neutral-300">
                    6 min read
                  </span>*/}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end mt-3 sm:mt-0 sm:ml-3 w-full">
              {/*<SocialsList className="mr-3"/>*/}
              {isAuthenticated && (currentUserId === post.author.userId || isAdmin) && (
                  <>
                    <ButtonPrimary
                        className="bg-blue-500 hover:bg-blue-700"
                        sizeClass="px-4 py-2 sm:px-5"
                        onClick={handleModify}
                    >
                      Modify
                    </ButtonPrimary>
                    <ButtonPrimary
                        className="bg-red-500 hover:bg-red-700 ml-2"
                        sizeClass="px-4 py-2 sm:px-5"
                        onClick={() => setIsDeleteModalOpen(true)}
                    >
                      Delete
                    </ButtonPrimary>
                  </>
              )}
              {/* Delete 모달 */}
              <DeleteModal
                  isOpen={isDeleteModalOpen}
                  onClose={() => setIsDeleteModalOpen(false)}
                  onConfirm={async () => {
                    await handleDelete();
                    setIsDeleteModalOpen(false);
                  }}
              />
            </div>
          </div>
        </div>
      </header>
    );
  };

  const renderContent = () => {
    return (
        <div
            id="single-entry-content"
            className="prose dark:prose-invert prose-sm !max-w-screen-md sm:prose lg:prose-lg mx-auto dark:prose-dark"
            dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(post.content, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder'] })}}
        >
        </div>
    );
  };

  const renderTags = () => {

    const tags = post.categories;

    return (
      <div className="max-w-screen-md mx-auto flex flex-wrap">
        {tags.length > 0 && (
            tags.map((category: any, index: number) => (
                <button
                    key={index}
                    style={{ cursor: 'default' }}
                    className="nc-Tag inline-block bg-white text-sm text-neutral-600 dark:text-neutral-300 py-2 rounded-lg border border-neutral-100 md:px-4 dark:bg-neutral-700 dark:border-neutral-700 hover:border-neutral-200 dark:hover:border-neutral-6000 mr-2 mb-2"
                >
                  {category.category}
                </button>
            ))
        )}
      </div>
    );
  };

  const renderAuthor = () => {
    return (
      <div className="max-w-screen-md mx-auto ">
        <div className="nc-SingleAuthor flex">
          <Avatar
              sizeClass="w-11 h-11 md:w-24 md:h-24"
              imgUrl={post.author.avatar}
          />
          <div className="flex flex-col ml-3 max-w-lg sm:ml-5 space-y-1">
            <span className="text-xs text-neutral-400 uppercase tracking-wider">
              WRITTEN BY
            </span>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-200">
              {post.author.displayName}
            </h2>
            <span className="text-sm text-neutral-500 sm:text-base dark:text-neutral-300">
              {post.author.desc
                  ? post.author.desc
                  : "The tech world moves fast, with innovation pushing boundaries every day."}
              {/*<a className="text-primary-6000 font-medium ml-1" href="/">
                Readmore
              </a>*/}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderCommentForm = () => {
    return (
        <div className="max-w-screen-md mx-auto pt-5">
          <h3 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200">
            Comments ({comments.length})
          </h3>
          <form className="nc-SingleCommentForm mt-5" onSubmit={handleCommentSubmit}>
            <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-neutral-400 dark:text-neutral-500">
                {isAuthenticated
                  ? `Commenting as ${user?.displayName || user?.name}`
                  : "Commenting anonymously"}
              </span>
              <div className="space-x-3">
                <ButtonPrimary type="submit" disabled={isSubmittingComment || !newComment.trim()}>
                  {isSubmittingComment ? "Posting..." : "Post"}
                </ButtonPrimary>
                <ButtonSecondary type="button" onClick={() => {
                  setNewComment("");
                  setReplyToId(null);
                }}>Cancel</ButtonSecondary>
              </div>
            </div>
          </form>
        </div>
    );
  };

  const renderComment = (comment: Comment) => {
    const replies = comments.filter(reply => reply.parent_id === comment.id);
    return (
        <li key={comment.id}>
          <BlogComment
              comment={comment}
              onDelete={() => handleCommentDelete(comment.id)}
              onReply={() => setReplyToId(comment.id)}
          />
          {replies.length > 0 && (
              <ul className="pl-4 mt-5 space-y-5 md:pl-11">
                {replies.map(renderComment)}
              </ul>
          )}
        </li>
    );
  };

  const renderCommentLists = () => {
    const topLevelComments = comments.filter(comment => comment.parent_id === null);
    return (
        <div className="max-w-screen-md mx-auto">
          {topLevelComments.length === 0 ? (
              <p className="text-neutral-400 dark:text-neutral-500 text-sm">
                No comments yet. Be the first to share your thoughts!
              </p>
          ) : (
              <ul className="nc-SingleCommentLists space-y-5">
                {topLevelComments.map(renderComment)}
              </ul>
          )}
        </div>
    );
  };

  /*const renderPostRelated = (post: PostDataType) => {
    return (
      <div
        key={post.id}
        className="relative aspect-w-3 aspect-h-4 rounded-3xl overflow-hidden group"
      >
        <Link to={post.href} />
        <NcImage
          className="object-cover w-full h-full transform group-hover:scale-105 transition-transform duration-300"
          src={post.featuredImage}
        />
        <div>
          <div className="absolute bottom-0 inset-x-0 h-1/2 bg-gradient-to-t from-black"></div>
        </div>
        <div className="flex flex-col justify-end items-start text-xs text-neutral-300 space-y-2.5 p-4">
          <Badge name="Categories" />
          <h2 className="block text-lg font-semibold text-white ">
            <span className="line-clamp-2">{post.title}</span>
          </h2>

          <div className="flex">
            <span className="block text-neutral-200 hover:text-white font-medium truncate">
              {post.author.displayName}
            </span>
            <span className="mx-1.5 font-medium">·</span>
            <span className="font-normal truncate">{post.date}</span>
          </div>
        </div>
        <Link to={post.href} />
      </div>
    );
  };*/

  return (
    <div className="nc-PageSingle pt-8 lg:pt-16 ">
      {renderHeader()}
      <NcImage
        className="w-full rounded-xl"
        containerClassName="container my-10 sm:my-12 max-w-4xl mx-auto"
        src={post.featuredImage}
      />

      <div className="nc-SingleContent container space-y-10">
        {renderContent()}
        {renderTags()}
        <div className="max-w-screen-md mx-auto border-b border-t border-neutral-100 dark:border-neutral-700"></div>
        {renderAuthor()}
        {renderCommentForm()}
        {renderCommentLists()}
      </div>
      <div className="relative bg-neutral-100 dark:bg-neutral-800 py-16 lg:py-28 mt-16 lg:mt-24">
        {/*<div className="container ">
          <h2 className="text-3xl font-semibold">Related posts</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">

            {DEMO_POSTS.filter((_, i) => i < 4).map(renderPostRelated)}

          </div>
        </div>*/}
      </div>
    </div>
  );
};

export default BlogSingle;
